import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authStorage } from '../../utils/storage';

// Field order + labels mirror the server's OFFICER_FIELDS / public form.
const FIELDS = [
  ['first_name', 'First name'],
  ['middle_name', 'Middle'],
  ['last_name', 'Last name'],
  ['email_primary', 'Personal email'],
  ['address_line1', 'Mailing address'],
  ['address_city', 'City/Town'],
  ['address_state', 'State'],
  ['address_zip', 'ZIP'],
  ['phone_cell', 'Cell phone'],
  ['phone_home', 'Home phone'],
  ['phone_work', 'Work phone'],
];

// Subtle per-officer accent, matching the public form.
const ACCENTS = {
  'President': '#315FAC', 'Vice President': '#0e7c7b', 'Secretary': '#6d54b5',
  'Corresponding Secretary': '#b5730f', 'Treasurer': '#1a7f4b',
};
const C4 = '#315FAC';

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: '20px 16px 90px', color: '#1b2733' },
  h1: { fontSize: '1.5rem', margin: '4px 0 2px', letterSpacing: '-.01em' },
  sub: { color: '#5b6b7a', margin: '0 0 20px', maxWidth: 720, lineHeight: 1.5 },
  tabs: { display: 'flex', gap: 4, background: '#eef1f5', padding: 4, borderRadius: 12, marginBottom: 22, width: 'fit-content' },
  tab: (a) => ({ padding: '8px 16px', cursor: 'pointer', border: 0, borderRadius: 9, fontSize: '.9rem', fontWeight: 600, color: a ? '#1b2733' : '#5b6b7a', background: a ? '#fff' : 'transparent', boxShadow: a ? '0 1px 3px rgba(16,24,40,.12)' : 'none' }),
  card: { background: '#fff', border: '1px solid #e3e9ef', borderRadius: 14, padding: 20, marginBottom: 14, boxShadow: '0 1px 2px rgba(16,24,40,.03)' },
  label: { display: 'block', fontSize: '.76rem', fontWeight: 700, color: '#5b6b7a', textTransform: 'uppercase', letterSpacing: '.03em', margin: '12px 0 5px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #d5dde5', borderRadius: 9, fontSize: '.95rem', boxSizing: 'border-box', background: '#fff' },
  btn: { background: C4, color: '#fff', border: 0, borderRadius: 9, padding: '10px 18px', fontWeight: 600, cursor: 'pointer', fontSize: '.92rem' },
  btnGhost: { background: '#fff', color: '#31435a', border: '1px solid #d5dde5', borderRadius: 9, padding: '9px 14px', fontWeight: 600, cursor: 'pointer', fontSize: '.9rem' },
  btnDanger: { background: '#fff', color: '#b3261e', border: '1px solid #e6b4b0', borderRadius: 9, padding: '9px 14px', fontWeight: 600, cursor: 'pointer', fontSize: '.9rem' },
  badge: (bg, fg) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: '.72rem', fontWeight: 700, background: bg, color: fg, whiteSpace: 'nowrap' }),
  msgOk: { background: '#e6f4ec', color: '#1a7f4b', border: '1px solid #bfe3cd', borderRadius: 9, padding: '11px 13px', margin: '10px 0' },
  msgErr: { background: '#fbeae9', color: '#b3261e', border: '1px solid #f2c5c1', borderRadius: 9, padding: '11px 13px', margin: '10px 0' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(18,28,42,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 60 },
  modal: { background: '#fff', maxWidth: 480, width: '100%', borderRadius: 16, padding: 24, boxShadow: '0 16px 48px rgba(16,24,40,.3)' },
};

function statusBadge(status) {
  const m = {
    pending: ['#fdf2d6', '#8a6d1a'], applied: ['#e6f4ec', '#1a7f4b'], approved: ['#e6f4ec', '#1a7f4b'],
    rejected: ['#fbeae9', '#b3261e'], needs_info: ['#eef4fb', '#274b7a'],
    awaiting: ['#fdf2d6', '#8a6d1a'], to_review: ['#e5eefc', '#274b7a'], responded: ['#e6f4ec', '#1a7f4b'], expired: ['#eef1f4', '#5b6b7a'],
  };
  const labels = { to_review: 'response to review', awaiting: 'awaiting response' };
  const [bg, fg] = m[status] || ['#eef1f4', '#5b6b7a'];
  return <span style={S.badge(bg, fg)}>{labels[status] || status}</span>;
}

const unitName = (r) => (r.LOCAL ? `Local ${r.LOCAL}${r.UNIT ? '-' + r.UNIT : ''}` : `Unit ${r.bargaining_unit_id}`) + (r.NAME ? `: ${r.NAME}` : '');

export default function OfficerChanges() {
  const token = authStorage.getToken();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);
  const [tab, setTab] = useState('queue');

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Officer Changes</h1>
      <p style={S.sub}>Review member-submitted officer updates and generate secure public update links for your locals. Nothing touches DataHouse until you approve it.</p>
      <div style={S.tabs}>
        <button style={S.tab(tab === 'queue')} onClick={() => setTab('queue')}>Review queue</button>
        <button style={S.tab(tab === 'sent')} onClick={() => setTab('sent')}>Sent links</button>
        <button style={S.tab(tab === 'link')} onClick={() => setTab('link')}>Generate link</button>
      </div>
      {tab === 'queue' && <ReviewQueue authHeaders={authHeaders} />}
      {tab === 'sent' && <SentLinks authHeaders={authHeaders} />}
      {tab === 'link' && <GenerateLink authHeaders={authHeaders} />}
    </div>
  );
}

function ReviewQueue({ authHeaders }) {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await fetch(`/api/officer-change-requests?status=${status}`, { headers: authHeaders });
      if (!r.ok) throw new Error('Failed to load queue');
      setRows(await r.json());
    } catch (e) { setErr(String(e.message || e)); }
  }, [status, authHeaders]);

  useEffect(() => { load(); }, [load]);

  if (selected) {
    return <ReviewDetail id={selected} authHeaders={authHeaders} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {['pending', 'needs_info', 'applied', 'rejected', 'all'].map((s) => (
          <button key={s} style={status === s ? S.btn : S.btnGhost} onClick={() => setStatus(s)}>{s.replace('_', ' ')}</button>
        ))}
        <button style={{ ...S.btnGhost, marginLeft: 'auto' }} onClick={load}>Refresh</button>
      </div>
      {err && <div style={S.msgErr}>{err}</div>}
      {rows.length === 0 && <p style={{ ...S.sub, margin: '8px 0' }}>No requests in this view.</p>}
      {rows.map((r) => (
        <div key={r.id} onClick={() => setSelected(r.id)}
          style={{ display: 'grid', gridTemplateColumns: '64px 1fr 150px 130px', gap: 12, alignItems: 'center', padding: '12px 8px', borderTop: '1px solid #eef1f4', cursor: 'pointer' }}>
          <div style={{ fontWeight: 700, color: '#5b6b7a' }}>#{r.id}</div>
          <div>
            <div style={{ fontWeight: 600 }}>{unitName(r)}</div>
            <div style={{ color: '#5b6b7a', fontSize: '.85rem' }}>{r.contact_name} ({r.contact_email})</div>
          </div>
          <div style={{ color: '#5b6b7a', fontSize: '.82rem' }}>{new Date(r.submitted_at).toLocaleDateString()}<br />{new Date(r.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
          <div>{statusBadge(r.status)}</div>
        </div>
      ))}
    </div>
  );
}

function ReviewDetail({ id, authHeaders, onBack }) {
  const [data, setData] = useState(null);
  const [edited, setEdited] = useState({});
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [infoMode, setInfoMode] = useState(false);
  const [infoTo, setInfoTo] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`/api/officer-change-requests/${id}`, { headers: authHeaders });
        if (!r.ok) throw new Error('Failed to load request');
        const j = await r.json();
        setData(j);
        setInfoTo(j.contact?.email || '');
        const seed = {};
        Object.entries(j.submitted || {}).forEach(([pos, f]) => { seed[pos] = { ...f }; });
        setEdited(seed);
      } catch (e) { setMsg({ err: String(e.message || e) }); }
    })();
  }, [id, authHeaders]);

  const setField = (pos, field, val) => setEdited((prev) => ({ ...prev, [pos]: { ...(prev[pos] || {}), [field]: val } }));

  const act = async (path, body, okText) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`/api/officer-change-requests/${id}/${path}`, { method: 'POST', headers: authHeaders, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((j.errors && j.errors.join(', ')) || j.message || 'Action failed');
      setMsg({ ok: okText || 'Saved.' });
      setTimeout(onBack, 1000);
    } catch (e) { setMsg({ err: String(e.message || e) }); } finally { setBusy(false); setConfirmApply(false); }
  };

  if (!data) return <div style={S.card}>{msg?.err ? <div style={S.msgErr}>{msg.err}</div> : 'Loading…'}</div>;
  const applied = data.status === 'applied';

  return (
    <div>
      <button style={S.btnGhost} onClick={onBack}>&larr; Back to queue</button>
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Request #{data.id}: {data.unitLabel}</h2>
          {statusBadge(data.status)}
        </div>
        <p style={{ ...S.sub, margin: '8px 0 0' }}>Submitted {new Date(data.submitted_at).toLocaleString()} by {data.contact.name} ({data.contact.email}){data.contact.phone ? `, ${data.contact.phone}` : ''}</p>
        {data.note && <div style={{ background: '#eef4fb', color: '#274b7a', border: '1px solid #d3e2f4', borderRadius: 9, padding: '10px 12px', marginTop: 12 }}><strong>Note from submitter:</strong> {data.note}</div>}
      </div>

      {msg?.err && <div style={S.msgErr}>{msg.err}</div>}
      {msg?.ok && <div style={S.msgOk}>{msg.ok}</div>}

      {(data.diff || []).map((d) => {
        const accent = ACCENTS[d.position] || C4;
        return (
          <div key={d.position} style={{ ...S.card, borderLeft: `4px solid ${accent}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: accent }}>{d.position}</h3>
              <span style={S.badge(d.status === 'unchanged' ? '#eef1f4' : '#fdf2d6', d.status === 'unchanged' ? '#5b6b7a' : '#8a6d1a')}>{d.status}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 1fr', gap: 10, fontWeight: 700, color: '#5b6b7a', fontSize: '.74rem', textTransform: 'uppercase', letterSpacing: '.03em', paddingBottom: 6, borderBottom: '1px solid #eef1f4' }}>
              <div>Field</div><div>Current</div><div>Submitted (editable)</div>
            </div>
            {FIELDS.map(([key, labelText]) => {
              const fieldDiff = d.fields.find((f) => f.field === key) || { from: '', to: '' };
              const val = (edited[d.position] && edited[d.position][key] !== undefined) ? edited[d.position][key] : fieldDiff.to;
              return (
                <div key={key} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 1fr', gap: 10, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f4f6f8', background: fieldDiff.changed ? 'rgba(253,242,214,.35)' : 'transparent' }}>
                  <div style={{ fontSize: '.82rem', color: '#5b6b7a' }}>{labelText}</div>
                  <div style={{ fontSize: '.9rem', color: '#5b6b7a' }}>{fieldDiff.from || <span style={{ opacity: .4 }}>(empty)</span>}</div>
                  <div><input style={S.input} value={val || ''} disabled={applied} onChange={(e) => setField(d.position, key, e.target.value)} /></div>
                </div>
              );
            })}
          </div>
        );
      })}

      {!applied && (
        <div style={S.card}>
          <label style={S.label}>Note (included in the email to the submitter)</label>
          <textarea style={{ ...S.input, minHeight: 70 }} value={note} onChange={(e) => setNote(e.target.value)} />
          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            <button style={S.btn} disabled={busy} onClick={() => setConfirmApply(true)}>Approve &amp; apply</button>
            <button style={S.btnGhost} disabled={busy} onClick={() => setInfoMode((v) => !v)}>Request more info</button>
            <button style={S.btnDanger} disabled={busy} onClick={() => act('reject', { review_note: note }, 'Rejected. The submitter was notified.')}>Reject</button>
          </div>
          {infoMode && (
            <div style={{ marginTop: 14, padding: 14, background: '#f7f9fb', border: '1px solid #e3e9ef', borderRadius: 10 }}>
              <label style={S.label}>Send the request to</label>
              <input style={S.input} value={infoTo} onChange={(e) => setInfoTo(e.target.value)} placeholder="who should provide the info" />
              <p style={{ fontSize: '.82rem', color: '#5b6b7a', margin: '6px 0 10px' }}>Defaults to the person who submitted. Change it to route the request to someone better suited.</p>
              <button style={S.btn} disabled={busy} onClick={() => act('request-info', { review_note: note, email_to: infoTo }, 'Request sent.')}>Send request for more info</button>
            </div>
          )}
        </div>
      )}

      {/* Officer change history for this local */}
      <div style={S.card}>
        <h3 style={{ margin: '0 0 4px', fontSize: '.98rem' }}>Officer history for this local</h3>
        <p style={{ ...S.sub, margin: '0 0 10px', fontSize: '.85rem' }}>The most recent officer changes on record, so you can see when each was last updated.</p>
        {(!data.history || data.history.length === 0) && <p style={{ color: '#5b6b7a', fontSize: '.85rem' }}>No prior officer changes recorded for this local.</p>}
        {(data.history || []).map((h, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 10, padding: '7px 0', borderTop: i ? '1px solid #f4f6f8' : 'none', fontSize: '.85rem' }}>
            <div style={{ color: '#5b6b7a' }}>{new Date(h.when).toLocaleDateString()} {new Date(h.when).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
            <div>
              {h.position && <strong>{h.position}</strong>} {h.name ? `— ${h.name}` : ''}
              <span style={{ color: '#5b6b7a' }}> · {h.action.replace(/_/g, ' ').toLowerCase()} by {h.who || 'unknown'}{h.source === 'officer_change_form' ? ' (via update form)' : ''}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Activity log for this request */}
      <div style={S.card}>
        <h3 style={{ margin: '0 0 8px', fontSize: '.98rem' }}>Activity log</h3>
        {(data.events || []).map((e, i) => (
          <div key={i} style={{ fontSize: '.82rem', color: '#5b6b7a', padding: '3px 0' }}>
            {new Date(e.created_at).toLocaleString()} &middot; <strong>{e.event_type}</strong> by {e.actor || 'public'}{e.ip ? ` (${e.ip})` : ''}
          </div>
        ))}
      </div>

      {confirmApply && (
        <div style={S.overlay} onClick={() => setConfirmApply(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.15rem' }}>Apply to DataHouse?</h2>
            <p style={{ color: '#31435a', lineHeight: 1.5 }}>Approving will write this officer information into DataHouse, where it becomes part of the local's record and is <strong>visible to all DataHouse users</strong>. This updates the live officer records.</p>
            <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
              <button style={S.btnGhost} onClick={() => setConfirmApply(false)}>Cancel</button>
              <button style={S.btn} disabled={busy} onClick={() => act('approve', { officers: edited, review_note: note }, 'Approved and applied to DataHouse.')}>Yes, apply to DataHouse</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SentLinks({ authHeaders }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await fetch('/api/officer-update-links/outstanding', { headers: authHeaders });
      if (!r.ok) throw new Error('Failed to load sent links');
      setRows(await r.json());
    } catch (e) { setErr(String(e.message || e)); }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    if (!window.confirm('Revoke this link? It will stop working immediately.')) return;
    await fetch(`/api/officer-update-links/${id}/revoke`, { method: 'POST', headers: authHeaders });
    load();
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Links that are out</h2>
        <button style={S.btnGhost} onClick={load}>Refresh</button>
      </div>
      <p style={{ ...S.sub, margin: '0 0 12px', fontSize: '.88rem' }}>Active links you have sent and their current state.</p>
      {err && <div style={S.msgErr}>{err}</div>}
      {rows.length === 0 && <p style={{ color: '#5b6b7a', fontSize: '.9rem' }}>No active links.</p>}
      {rows.map((r) => (
        <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr 150px auto', gap: 12, alignItems: 'center', padding: '12px 6px', borderTop: '1px solid #eef1f4' }}>
          <div>
            <div style={{ fontWeight: 600 }}>{r.unitLabel}</div>
            <div style={{ color: '#5b6b7a', fontSize: '.82rem' }}>
              Sent {new Date(r.created_at).toLocaleDateString()} by {r.created_by}
              {r.has_password ? ' · password protected' : ''}
              {r.expires_at ? ` · expires ${new Date(r.expires_at).toLocaleDateString()}` : ''}
            </div>
          </div>
          <div>{statusBadge(r.state)}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.btnGhost} onClick={() => { navigator.clipboard && navigator.clipboard.writeText(r.url); setCopied(r.id); setTimeout(() => setCopied(null), 1500); }}>{copied === r.id ? 'Copied' : 'Copy link'}</button>
            <button style={S.btnDanger} onClick={() => revoke(r.id)}>Revoke</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function GenerateLink({ authHeaders }) {
  const [units, setUnits] = useState([]);
  const [unitFilter, setUnitFilter] = useState('');
  const [form, setForm] = useState({ bargaining_unit_id: '', expires_in_days: 30, password: '', prefill_enabled: false, max_uses: 1, email_to: '', note: '' });
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/officer-update-links/assignable-units', { headers: authHeaders });
        if (r.ok) setUnits(await r.json());
      } catch { /* ignore */ }
    })();
  }, [authHeaders]);

  const filtered = useMemo(() => {
    const q = unitFilter.trim().toLowerCase();
    if (!q) return units.slice(0, 500);
    return units.filter((u) => u.label.toLowerCase().includes(q)).slice(0, 500);
  }, [units, unitFilter]);

  const submit = async () => {
    setBusy(true); setMsg(null); setResult(null);
    try {
      if (!form.bargaining_unit_id) throw new Error('Choose a unit first.');
      if (form.prefill_enabled && !form.password) throw new Error('Prefill requires a password on the link.');
      const r = await fetch('/api/officer-update-links', { method: 'POST', headers: authHeaders, body: JSON.stringify({ ...form, bargaining_unit_id: parseInt(form.bargaining_unit_id, 10) }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.message || 'Failed to create link');
      setResult(j);
    } catch (e) { setMsg({ err: String(e.message || e) }); } finally { setBusy(false); }
  };

  return (
    <div style={S.card}>
      <h2 style={{ margin: '0 0 4px', fontSize: '1.1rem' }}>Generate a public update link</h2>
      <p style={{ ...S.sub, margin: '0 0 14px', fontSize: '.9rem' }}>Pick one of your locals. You can email the link from here, or copy it and send it however you like (text, email, etc.). Prefilling current officers requires a password so private info is never on an open link.</p>
      {msg?.err && <div style={S.msgErr}>{msg.err}</div>}

      <label style={S.label}>Local / bargaining unit *</label>
      <input style={{ ...S.input, marginBottom: 6 }} value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)} placeholder="Type to filter your locals…" />
      <select style={S.input} value={form.bargaining_unit_id} onChange={(e) => set('bargaining_unit_id', e.target.value)} size={Math.min(8, Math.max(3, filtered.length))}>
        <option value="">{units.length ? `Select a local (${units.length} available)` : 'No assigned locals found'}</option>
        {filtered.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
      </select>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={S.label}>Expires in (days)</label><input style={S.input} type="number" value={form.expires_in_days} onChange={(e) => set('expires_in_days', e.target.value)} /></div>
        <div><label style={S.label}>Max uses</label><input style={S.input} type="number" value={form.max_uses} onChange={(e) => set('max_uses', e.target.value)} /></div>
      </div>
      <label style={S.label}>Password (optional; required for prefill)</label>
      <input style={S.input} value={form.password} onChange={(e) => set('password', e.target.value)} />
      <label style={{ ...S.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', textTransform: 'none', fontSize: '.88rem', letterSpacing: 0 }}>
        <input type="checkbox" checked={form.prefill_enabled} onChange={(e) => set('prefill_enabled', e.target.checked)} style={{ width: 'auto' }} />
        Prefill current officer info (password required)
      </label>
      <label style={S.label}>Email the link to (optional)</label>
      <input style={S.input} value={form.email_to} onChange={(e) => set('email_to', e.target.value)} placeholder="member@example.com" />
      <label style={S.label}>Internal note (optional)</label>
      <input style={S.input} value={form.note} onChange={(e) => set('note', e.target.value)} />
      <div style={{ marginTop: 16 }}><button style={S.btn} disabled={busy} onClick={submit}>Create link</button></div>

      {result && (
        <div style={{ ...S.msgOk, marginTop: 16 }}>
          <strong>Link created.</strong>
          <div style={{ marginTop: 6, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '.85rem' }}>{result.url}</div>
          <button style={{ ...S.btnGhost, marginTop: 8 }} onClick={() => { navigator.clipboard && navigator.clipboard.writeText(result.url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? 'Copied' : 'Copy link'}</button>
        </div>
      )}
    </div>
  );
}
